import { IInteriorProduct, IInteriorProductSubGroup, IVec2d } from "../../models/IChainInfo";
import { add, cheapDist, intersect, IObj2d } from "../../models/IObj";
import { min } from "../../util/arraySlice";
import { InteriorProductType } from "../WebApiClient";
import { DragInfo, DragService, DragServiceProps } from "./DragService";
import { Section } from "./Section";

interface Pos extends IObj2d {
    section: Section;
    product: IInteriorProduct;
    mouseTarget: IVec2d;
}


export class DragServiceSnapLeftAndRight extends DragService {

    private readonly possiblePositions: Pos[];

    constructor(props: DragServiceProps) {
        super(props);

        
        this.possiblePositions = this.getPossiblePositions(props);
    }

    private getPossiblePositions(props: DragServiceProps) {
        const result: Pos[] = [];
        for (let sect of this.baseState.sections) {
            const product = this.productSubGroup.products
                .find(p => this.isMatching(p, sect));
            if (!product)
                continue;

            const sectionResult: Pos[] = [];
            for (let y = sect.position.y + sect.stepOffset - product.snapOffset; sect.position.y + sect.size.y >= y + product.defaultSize.y; y += sect.stepHeight) {
                if (y < sect.position.y)
                    continue;
                const obj: IObj2d = {
                    position: { x: sect.position.x, y: y },
                    size: { x: sect.size.x, y: product.defaultSize.y },
                };
                const collidingObject = this.baseState.otherItems
                    .filter(i => !(i.product.productSubGroup.productType === InteriorProductType.Module))
                    .find(otherItem => intersect(obj, otherItem.item))
                if (collidingObject) {
                    continue;
                }
                sectionResult.push({
                    ...obj,
                    product: product,
                    section: sect,
                    mouseTarget: add(obj.position, props.mouseOffset)
                });
            }
            result.push(...sectionResult);
        }
        return result;
    }

    getDragInfo(cursorModelPos: IVec2d): DragInfo | undefined {

        const pos = min(this.possiblePositions, pos => cheapDist(pos.mouseTarget, cursorModelPos));
        if (!pos)
            return undefined;
        const parentIndexes = new Set( [pos.section.leftGable, pos.section.rightGable]
            .map(gab => gab?.item.index!)
            .filter(i => i !== undefined));

        const productMaterial = pos.product.materials.find(m => m.interiorMaterialId === this.cabinet.interiorMaterialId);
        const textureUrl = productMaterial?.drawingPath ?? pos.product.textureUrl;
        return {
            position: pos.position,
            productId: pos.product.id,
            size: pos.size,
            shadows: [],
            textureUrl: textureUrl,
            parentIndexes: [...parentIndexes]
        };
    }

    static acceptable(pt: IInteriorProductSubGroup): boolean {
        return (
            pt.productType === InteriorProductType.GableShelf
            || pt.productType === InteriorProductType.ShelfOrDrawer
            || pt.productType === InteriorProductType.ModuleShelf
            );
    }

    private isMatching(product: IInteriorProduct, section: Section): boolean {
        return this.isSizeMatching(product, section)
            && this.isTypeMatching(product, section);
    }

    private isSizeMatching(product: IInteriorProduct, section: Section): boolean {
        const sizeTolerance = 1;
        const sizeDifference = Math.abs(product.defaultSize.x - section.size.x);

        if (sizeDifference <= sizeTolerance)
            return true;
        return product.minSize.x <= section.size.x
            && product.maxSize.x >= section.size.x
    }

    private isTypeMatching(product: IInteriorProduct, section: Section): boolean {
        if (product.productType === InteriorProductType.ShelfOrDrawer)
            return true;
        if (section.isModule) {
            return product.productType === InteriorProductType.ModuleShelf;
        }
        else {
            return product.productType === InteriorProductType.GableShelf;
        }
    }
}
